import { useState, useEffect } from 'react'
import styles from './PublicDishesCard.module.css'
import arrowPrev from '../../assets/icons/arrowLeft.svg'
import arrowNext from '../../assets/icons/arrowRight.svg'
import noImage from '../../assets/img/no-image.jpg'
import { Slide } from 'react-slideshow-image'
import CustomButton from '../UI/CustomButton/CustomButton'
import { useAppDispatch, useAppSelector } from '../../redux/types/types'
import {
  addProductToOrder,
  updateProductQuantity,
  PublicOrderItem,
} from '../../redux/reducers/publicOrder/publicOrderSlice'

interface PublicDishesCardProps {
  name: string
  images: string[]
  price: number
  description: string
  weight: number | string
  measurement: string
  id: number
  categoryId: number
  categoryName: string
  categoryType?: string
}

const formatMeasurement = (weight: number | string, measurement: string): string => {
  const measurementMap: { [key: string]: string } = {
    миллилитров: 'мл',
    миллилитр: 'мл',
    литров: 'л',
    литр: 'л',
    граммов: 'г',
    грамм: 'г',
    килограммов: 'кг',
    килограмм: 'кг',
    штук: 'шт',
    штука: 'шт',
    порций: 'порц',
    порция: 'порц',
    упаковка: 'уп',
    упаковки: 'уп',
  }

  const shortMeasurement = measurementMap[measurement.toLowerCase()] || measurement
  return `${weight} ${shortMeasurement}`
}

const PublicDishesCard: React.FC<PublicDishesCardProps> = ({
  name,
  images,
  price,
  description,
  weight,
  measurement,
  id,
  categoryId,
  categoryName,
  categoryType,
}) => {
  const dispatch = useAppDispatch()
  const { items: publicOrderItems } = useAppSelector((state) => state.publicOrder)

  const currentPublicOrderItem = publicOrderItems.find((item) => item.productId === id)
  const [inputValue, setInputValue] = useState<string>('1')

  useEffect(() => {
    if (currentPublicOrderItem) {
      setInputValue(currentPublicOrderItem.quantity.toString())
    } else {
      setInputValue('1')
    }
  }, [currentPublicOrderItem])

  const handleArrowClick = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  const properties = {
    canSwipe: false,
    duration: 5000,
    autoplay: false,
    transitionDuration: 500,
    arrows: false,
    infinite: true,
    easing: 'ease',
    indicators: () => <div className='sliderIndicator'></div>,
    prevArrow: (
      <div className={styles.arrowPrevBlock} onClick={handleArrowClick}>
        <img className={styles.arrowPrev} src={arrowPrev} alt='arrowPrev' />
      </div>
    ),
    nextArrow: (
      <div className={styles.arrowNextBlock} onClick={handleArrowClick}>
        <img className={styles.arrowNext} src={arrowNext} alt='arrowNext' />
      </div>
    ),
  }

  const handleIncrease = () => {
    if (currentPublicOrderItem) {
      dispatch(
        updateProductQuantity({
          productId: id,
          quantity: currentPublicOrderItem.quantity + 1,
        }),
      )
    }
  }

  const handleDecrease = () => {
    if (currentPublicOrderItem && currentPublicOrderItem.quantity > 1) {
      dispatch(
        updateProductQuantity({
          productId: id,
          quantity: currentPublicOrderItem.quantity - 1,
        }),
      )
    }
  }

  const onCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setInputValue(value)

    const val = Number(value)

    if (!isNaN(val) && val >= 1) {
      dispatch(
        updateProductQuantity({
          productId: id,
          quantity: val,
        }),
      )
    }
  }

  const handleAddToOrder = () => {
    const productData: PublicOrderItem = {
      productId: id,
      name: name,
      quantity: 1,
      price: price.toString(),
      category: {
        id: categoryId,
        name: categoryName,
      },
      images: images,
    }

    dispatch(addProductToOrder({ product: productData }))
  }

  return (
    <div className={styles.dishesCard}>
      <div className={styles.left}>
        <div className={styles.slider}>
          {images.length !== 0 ? (
            <Slide {...properties} cssClass='sliderContainer'>
              {images.map((slideImage, index) => (
                <div
                  key={index}
                  className={styles.slideImage}
                  style={{
                    backgroundImage: `url('${process.env.REACT_APP_BASE_URL_IMAGES}/${slideImage}')`,
                    zIndex: 0,
                  }}
                ></div>
              ))}
            </Slide>
          ) : (
            <div className={styles.slideImage}>
              <img className={styles.slideImage} src={noImage} alt='noImage' />
            </div>
          )}
        </div>
      </div>
      <div className={styles.center}>
        <div className={styles.top}>
          <h3 className={styles.name}>{name}</h3>
          <h5 className={styles.description}>{description}</h5>
        </div>
      </div>

      <div className={styles.plusButtonBlock}>
        <div className={styles.price}>
          <h3>
            {categoryType === 'rent' && 'Аренда'} {price} руб
          </h3>
          <h5 className={styles.weight}>{formatMeasurement(weight, measurement)}</h5>
        </div>

        {currentPublicOrderItem ? (
          <div className={styles.countBlock}>
            <CustomButton className={styles.countButton} title='-' onClick={handleDecrease} />
            <input
              min={1}
              value={inputValue}
              onChange={onCountChange}
              className={styles.countInput}
            />
            <CustomButton className={styles.countButton} title='+' onClick={handleIncrease} />
          </div>
        ) : (
          <>
            <CustomButton title='Добавить' className={styles.addBtn} onClick={handleAddToOrder} />
          </>
        )}
      </div>
    </div>
  )
}

export default PublicDishesCard
